// components/maintenance/MaintenanceScreen.tsx
import React from "react";
import { useTranslation } from "react-i18next";
import { Wrench, RefreshCw, LogOut } from "lucide-react";
import { supabase } from "../../lib/supabaseClient";
import { useLanguageStore } from "../../state/useLanguageStore";
import { useLocaleFormat } from "../../hooks/useLocaleFormat";
import { pickMaintenanceMessage, type MaintenanceStatus } from "../../utils/maintenance";

interface MaintenanceScreenProps {
  status: MaintenanceStatus;
  onRetry?: () => void;
  isRetrying?: boolean;
}

/** Écran bloquant plein page affiché aux utilisateurs non exemptés pendant la maintenance. */
const MaintenanceScreen: React.FC<MaintenanceScreenProps> = ({
  status,
  onRetry,
  isRetrying = false,
}) => {
  const { t } = useTranslation("common");
  const language = useLanguageStore((s) => s.language);
  const { formatDateTime } = useLocaleFormat();
  const message = pickMaintenanceMessage(status, language) ?? t("maintenance.default_message");

  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center px-4 py-10 text-white">
      <div className="w-full max-w-lg rounded-3xl border border-amber-400/30 bg-amber-500/5 p-8 text-center shadow-[0_40px_90px_-60px_rgba(245,158,11,0.7)] backdrop-blur">
        <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-full bg-amber-500/20 text-amber-200">
          <Wrench className="h-8 w-8" aria-hidden />
        </div>
        <h1 className="text-2xl font-semibold">{t("maintenance.title")}</h1>
        <p className="mt-3 whitespace-pre-line text-sm leading-relaxed text-white/80">
          {message}
        </p>
        {status.ends_at && (
          <p className="mt-4 text-xs uppercase tracking-wide text-amber-200/80">
            {t("maintenance.expected_end", { date: formatDateTime(status.ends_at) })}
          </p>
        )}
        <div className="mt-8 flex flex-col items-center justify-center gap-2 sm:flex-row">
          {onRetry && (
            <button
              type="button"
              onClick={onRetry}
              disabled={isRetrying}
              className="inline-flex items-center gap-2 rounded-lg bg-amber-500/90 px-4 py-2 text-sm font-medium text-slate-950 transition hover:bg-amber-400 disabled:opacity-60"
            >
              <RefreshCw className={`h-4 w-4 ${isRetrying ? "animate-spin" : ""}`} aria-hidden />
              {t("maintenance.retry")}
            </button>
          )}
          <button
            type="button"
            onClick={() => {
              supabase.auth.signOut().catch((e) =>
                console.error("[MaintenanceScreen] Logout error:", e)
              );
            }}
            className="inline-flex items-center gap-2 rounded-lg border border-white/15 px-4 py-2 text-sm font-medium text-white/80 transition hover:bg-white/10"
          >
            <LogOut className="h-4 w-4" aria-hidden />
            {t("maintenance.sign_out")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default MaintenanceScreen;
